import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { Gantt, type GanttRow } from '../../components/Gantt';
import { Alert, Badge, Card, Checkbox, Select, Spinner } from '../../components/ui';
import { type MessageKey, t } from '../../i18n';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { errorMessage } from '../../lib/errors';
import { can } from '../../lib/permissions';
import { formatDay, isOpen, taskTone, todayIso } from '../projects/data';
import { statusTone as ticketTone, type TicketListItem } from '../tickets/data';
import type { TaskListItem } from './data';

const GROUPS = ['fecha', 'proyecto', 'estado'] as const;
type Group = (typeof GROUPS)[number];
const VIEWS = ['lista', 'cronograma'] as const;
type View = (typeof VIEWS)[number];
const WHEN = ['vencidas', 'hoy', 'semana', 'despues', 'sin_fecha'] as const;

/** Un elemento de la vista unificada: una tarea (propia o de un proyecto) o un ticket asignado. */
interface Item {
  key: string;
  kind: 'tarea' | 'ticket';
  title: string;
  href: string;
  /** Para agrupar y ordenar: el vencimiento de la tarea o del SLA del ticket. */
  due: string | null;
  start: string | null;
  status: string;
  open: boolean;
  overdue: boolean;
  context: string;
  badge: 'slate' | 'blue' | 'amber' | 'red';
}

/** Fecha local AAAA-MM-DD de un instante. */
const localDay = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Domingo de la semana de `today` (la semana va de lunes a domingo). */
function endOfWeek(today: string): string {
  const d = new Date(`${today}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + ((7 - d.getUTCDay()) % 7));
  return d.toISOString().slice(0, 10);
}

/**
 * Vista unificada (guía 7.3): las tareas propias, las de proyectos asignadas y, para IT, los tickets asignados, en una
 * lista agrupable o en la línea de tiempo.
 */
export function MyTasksPage() {
  const me = useMe();
  const it = can(me, 'tickets.gestionar');
  const [params, setParams] = useSearchParams();
  const group: Group = (GROUPS as readonly string[]).includes(params.get('agrupar') ?? '') ? (params.get('agrupar') as Group) : 'fecha';
  const view: View = (VIEWS as readonly string[]).includes(params.get('vista') ?? '') ? (params.get('vista') as View) : 'lista';
  const withDone = params.get('terminadas') === 'si';

  const tasks = useQuery({
    queryKey: ['tareas', 'lista', 'mias', withDone],
    queryFn: () => api<TaskListItem[]>(`/api/tareas?alcance=mias${withDone ? '' : '&estado=abiertas'}`),
  });
  const tickets = useQuery({
    queryKey: ['tickets', 'lista', 'asignados', withDone ? 'todos' : 'abiertos'],
    queryFn: () => api<TicketListItem[]>(`/api/tickets?alcance=asignados${withDone ? '' : '&estado=abiertos'}`),
    enabled: it,
  });

  const update = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(changes)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };

  const error = tasks.error ?? tickets.error;
  if (!tasks.data || (it && !tickets.data)) return error ? <Alert>{errorMessage(error)}</Alert> : <Spinner label={t('comun.cargando')} />;

  const today = todayIso();
  const items: Item[] = [
    ...tasks.data.map((x) => ({
      key: `t-${x.id}`,
      kind: 'tarea' as const,
      title: x.title,
      href: `/tareas/${x.id}`,
      due: x.dueDate,
      start: x.startDate,
      status: x.status,
      open: isOpen(x.statusCategory),
      overdue: x.overdue,
      context: x.projectCode ? `${x.projectCode} · ${x.projectName ?? ''}` : t('ta.personal'),
      badge: taskTone(x.statusCategory),
    })),
    ...(tickets.data ?? []).map((x) => {
      const due = x.slaDueAt ? localDay(x.slaDueAt) : null;
      const open = x.statusCategory === 'en_cola' || x.statusCategory === 'en_curso';
      return {
        key: `k-${x.id}`,
        kind: 'ticket' as const,
        title: x.title,
        href: `/tickets/${x.number}`,
        due,
        start: null,
        status: x.status,
        open,
        overdue: open && !x.slaPausedSince && !!x.slaDueAt && new Date(x.slaDueAt).getTime() < Date.now(),
        context: `${x.code} · ${t('ta.ticket')}`,
        badge: ticketTone(x.statusCategory),
      };
    }),
  ].sort((a, b) => (a.due ?? '9999').localeCompare(b.due ?? '9999') || a.title.localeCompare(b.title, 'es'));

  const weekEnd = endOfWeek(today);
  const whenOf = (i: Item): (typeof WHEN)[number] => (!i.due ? 'sin_fecha' : i.overdue || (i.open && i.due < today) ? 'vencidas' : i.due === today ? 'hoy' : i.due <= weekEnd ? 'semana' : 'despues');
  const groups: Array<{ key: string; label: string; items: Item[] }> = [];
  const push = (key: string, label: string, item: Item) => {
    let g = groups.find((x) => x.key === key);
    if (!g) groups.push((g = { key, label, items: [] }));
    g.items.push(item);
  };
  for (const i of items) {
    if (group === 'fecha') push(whenOf(i), t(`ta.cuando_${whenOf(i)}` as MessageKey), i);
    else if (group === 'proyecto') push(i.kind === 'ticket' ? 'zz-tickets' : i.context, i.kind === 'ticket' ? t('ta.tickets') : i.context, i);
    else push(i.status, i.status, i);
  }
  if (group === 'fecha') groups.sort((a, b) => WHEN.indexOf(a.key as (typeof WHEN)[number]) - WHEN.indexOf(b.key as (typeof WHEN)[number]));
  else groups.sort((a, b) => a.key.localeCompare(b.key, 'es'));

  const ganttRows: GanttRow[] = items.map((i) => ({
    id: i.key,
    label: i.title,
    sublabel: i.context,
    depth: 0,
    start: i.start,
    end: i.due,
    tone: !i.open ? 'done' : i.overdue ? 'late' : i.badge === 'blue' ? 'active' : 'todo',
    href: i.href,
  }));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-tinta">{t('ta.titulo')}</h1>
          <p className="max-w-2xl text-sm text-slate-600">{it ? t('ta.descripcion_it') : t('ta.descripcion_vista')}</p>
        </div>
        <div className="flex gap-2">
          <Link to="/tareas/resumen" className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            {t('ta.resumen')}
          </Link>
          <Link to="/tareas/nueva" className="rounded bg-marca px-4 py-2 text-sm font-medium text-white hover:bg-marca-claro">
            {t('ta.nueva')}
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div role="tablist" aria-label={t('ta.vistas')} className="flex gap-1">
          {VIEWS.map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={v === view}
              className={`rounded-md px-3 py-2 text-sm ${v === view ? 'bg-marca text-white' : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'}`}
              onClick={() => update({ vista: v === 'lista' ? null : v })}
            >
              {t(`ta.vista_${v}` as MessageKey)}
            </button>
          ))}
        </div>
        {view === 'lista' && (
          <div className="w-44">
            <Select id="ta-agrupar" label={t('ta.agrupar')} value={group} onChange={(e) => update({ agrupar: e.target.value === 'fecha' ? null : e.target.value })}>
              {GROUPS.map((g) => (
                <option key={g} value={g}>
                  {t(`ta.agrupar_${g}` as MessageKey)}
                </option>
              ))}
            </Select>
          </div>
        )}
        <div className="pb-2">
          <Checkbox label={t('ta.incluir_terminadas')} checked={withDone} onChange={(e) => update({ terminadas: e.target.checked ? 'si' : null })} />
        </div>
      </div>

      {items.length === 0 ? (
        <Card>
          <p className="text-sm text-slate-500">{t('ta.vacio')}</p>
        </Card>
      ) : view === 'cronograma' ? (
        <Gantt rows={ganttRows} today={today} label={t('ta.vista_cronograma')} />
      ) : (
        <div className="space-y-4">
          {groups.map((g) => (
            <Card key={g.key} title={`${g.label} (${g.items.length})`} className={g.key === 'vencidas' ? 'border-red-200' : ''}>
              <ul className="divide-y divide-slate-100" aria-label={g.label}>
                {g.items.map((i) => (
                  <li key={i.key} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm">
                    <div className="min-w-0">
                      <Link to={i.href} className={`font-medium hover:underline ${i.open ? 'text-slate-900' : 'text-slate-500 line-through'}`}>
                        {i.title}
                      </Link>
                      <span className="block text-xs text-slate-500">{i.context}</span>
                    </div>
                    <span className="flex items-center gap-2 text-xs text-slate-500">
                      {i.due && <span className={i.overdue ? 'font-medium text-red-700' : ''}>{i.kind === 'ticket' ? t('ta.sla_vence', { fecha: formatDay(i.due) }) : formatDay(i.due)}</span>}
                      <Badge tone={i.overdue ? 'red' : i.badge}>{i.status}</Badge>
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
